import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { RxCross1 } from "react-icons/rx";
import {
  EVENT_BANNER_ASPECT,
  EVENT_BANNER_SIZE_LABEL,
} from "../../constants/eventBanner";
import { getCroppedEventBannerFile } from "../../utils/cropEventBannerImage";

const EventBannerCropModal = ({
  open,
  imageSrc,
  originalFileName,
  onConfirm,
  onCancel,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const onCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels || !imageSrc) return;
    setIsSaving(true);
    try {
      const file = await getCroppedEventBannerFile(
        imageSrc,
        croppedAreaPixels,
        originalFileName
      );
      onConfirm(file);
    } catch {
      onCancel();
    } finally {
      setIsSaving(false);
    }
  };

  if (!open || !imageSrc) return null;

  return (
    <div
      className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/60 px-4 py-6"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSaving) onCancel();
      }}
    >
      <div
        className="flex w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Crop banner image</h3>
            <p className="mt-1 text-sm text-slate-600">
              Drag and zoom to position your image inside the{" "}
              <span className="font-medium">{EVENT_BANNER_SIZE_LABEL}</span> frame.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close cropper"
            onClick={onCancel}
            disabled={isSaving}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
          >
            <RxCross1 size={14} />
          </button>
        </div>

        <div className="relative h-[min(50vh,360px)] w-full bg-slate-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={EVENT_BANNER_ASPECT}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid
            objectFit="horizontal-cover"
          />
        </div>

        <div className="space-y-3 border-t border-slate-200 px-5 py-4">
          <label className="flex items-center gap-3 text-sm text-slate-700">
            <span className="shrink-0 font-medium">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-[#38513b]"
            />
          </label>
          <p className="text-xs text-slate-500">
            The highlighted area is what will be saved. All banners are exported at{" "}
            {EVENT_BANNER_SIZE_LABEL}.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSaving}
              className="h-[42px] flex-1 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isSaving || !croppedAreaPixels}
              className="h-[42px] flex-1 rounded-lg bg-[#38513b] text-sm font-semibold text-white hover:bg-[#2f4232] disabled:opacity-50"
            >
              {isSaving ? "Saving…" : "Use cropped image"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventBannerCropModal;
